import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileCheck2, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

/**
 * Global top-bar pill (next to the bell) listing completed transcript parses
 * across all the user's estimates that are awaiting review, so nothing slips
 * through across deals. Hidden when there are none.
 */
export default function TranscriptReadyPill() {
  const navigate = useNavigate();
  const [ready, setReady] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let mounted = true;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/transcript-jobs/ready`, { withCredentials: true });
        if (mounted) setReady(r.data?.ready || []);
      } catch { /* non-blocking */ }
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (ready.length === 0) return null;

  const goTo = (item) => {
    setOpen(false);
    navigate(`/estimator/${item.estimate_id}/requirements`);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-700 text-xs font-medium hover:bg-emerald-100 transition-colors"
        title="Transcripts ready to review"
        data-testid="transcript-ready-pill"
      >
        <FileCheck2 className="w-3.5 h-3.5" />
        <span data-testid="transcript-ready-count">{ready.length}</span>
        <span className="hidden sm:inline">ready</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 bg-white rounded-lg border border-slate-200 shadow-xl z-50 overflow-hidden"
          data-testid="transcript-ready-menu"
        >
          <div className="px-3 py-2 border-b border-slate-100 text-[11px] uppercase tracking-wide font-semibold text-slate-500">
            Transcripts ready to review
          </div>
          <ul className="max-h-80 overflow-y-auto">
            {ready.map((item) => (
              <li key={item.job_id}>
                <button
                  onClick={() => goTo(item)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
                  data-testid="transcript-ready-item"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#0F2040] truncate">{item.estimate_name}</div>
                    <div className="text-[11px] text-slate-400 truncate">
                      {item.customer_name ? `${item.customer_name} · ` : ''}{item.filename || 'transcript'} · {timeAgo(item.updated_at)}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
